import { computed } from 'vue';
import useAccounts from '@/composables/accounts/useAccounts';
import { useBalanceRefresh } from '@/composables/useBalanceRefresh';
import { useAllowanceRefresh } from '@/composables/useAllowanceRefresh';
import { useBalanceAndAllowance } from '@/composables/useBalanceAndAllowance';
import { logDEBUG } from '@/common/debug';
import { logWarn } from '@/common/helpers';
import { ACCOUNT_PROVIDER_ID } from '@/composables/accounts/providers/types.ts';
import {
  registerPredictionPlacedCallback,
  registerMarketCreatedCallback,
  registerMarketSettledCallback,
  registerMarketCancelledCallback,
  registerCreatorRegisteredCallback,
  registerCreatorUpdatedCallback,
  registerTokenApprovedCallback,
  registerTokenTransferredCallback,
  setGetCurrentUserAddressFn,
  setTriggerBalanceRefreshFn,
} from '@/composables/blockchainEventCallbacks';
import { BALANCE_REFRESH_DELAY_MS, WEBSOCKET_EVENT_TIMEOUT_MS } from '@/common/config';
import { getWsConnectionQuality, getWsIsSubscribed } from '@/common/predictionMarkets/predictionMarkets';

// Market event types (PlacedPrediction, CreatedMarket, SettledMarket, CancelledMarket)
// AND Creator event types (CreatedCreator, UpdatedCreator)
// AND Token event types (Approval, Transfer)
// are ALL handled by EvmPredictionMarketProvider's GLOBAL listeners via a single WebSocket connection.
// The provider monitors ALL events and triggers user-specific callbacks when appropriate.
//
// This composable is now a thin wrapper around the callback registration system.

type TransactionEventCallback = () => void;

// Lazy-init for composables (to avoid circular dependencies)
let balanceRefreshTrigger: (() => void) | null = null;
let allowanceRefreshTrigger: (() => void) | null = null;
let clearBalanceCache: (() => void) | null = null;

function getRefreshTriggers() {
  if (!balanceRefreshTrigger) {
    const { triggerRefresh: triggerBalanceRefresh } = useBalanceRefresh();
    balanceRefreshTrigger = triggerBalanceRefresh;
  }
  if (!allowanceRefreshTrigger) {
    const { triggerRefresh: triggerAllowanceRefresh } = useAllowanceRefresh();
    allowanceRefreshTrigger = triggerAllowanceRefresh;
  }
  return { triggerBalanceRefresh: balanceRefreshTrigger, triggerAllowanceRefresh: allowanceRefreshTrigger };
}

function getClearCacheFunction() {
  if (!clearBalanceCache) {
    const { clearCache } = useBalanceAndAllowance();
    clearBalanceCache = clearCache;
  }
  return clearBalanceCache;
}

/**
 * Composable for subscribing to blockchain events via WebSocket for instant updates
 *
 * **Architecture:**
 * - All blockchain events (market, creator, token) are subscribed by EvmPredictionMarketProvider
 * - Provider uses a SINGLE WebSocket connection to avoid duplication
 * - This composable is a thin wrapper for registering callbacks
 * - Connection quality monitoring is centralized in the provider
 *
 * **Usage:**
 * ```typescript
 * const blockchainEvents = useBlockchainEvents();
 *
 * // Subscribe to events (sets up helper functions for provider)
 * await blockchainEvents.subscribe();
 *
 * // Register callback for instant transaction success notification
 * const cleanup = blockchainEvents.onPredictionPlaced(() => {
 *   showSuccessNotification('Prediction placed!');
 * });
 *
 * // Execute transaction with instant feedback
 * await blockchainEvents.executeWithWebSocketEvent(
 *   blockchainEvents.onPredictionPlaced,
 *   async () => await makePrediction(...),
 *   () => router.push('/predictions')
 * );
 *
 * // Check connection quality
 * console.log(blockchainEvents.connectionQuality.value); // 'excellent' | 'good' | 'poor' | 'disconnected'
 *
 * // Unsubscribe (cleanup)
 * blockchainEvents.unsubscribe();
 * ```
 */
export function useBlockchainEvents() {
  const accounts = useAccounts();

  // Set up helper functions for the callback registry (used by EvmPredictionMarketProvider)
  // This lets the provider trigger user-specific callbacks without circular dependencies
  setGetCurrentUserAddressFn(() => accounts.selectedAccount.value?.address || null);
  setTriggerBalanceRefreshFn(() => {
    const { triggerBalanceRefresh, triggerAllowanceRefresh } = getRefreshTriggers();
    const clearCache = getClearCacheFunction();
    clearCache();
    setTimeout(() => {
      triggerBalanceRefresh();
      triggerAllowanceRefresh();
    }, BALANCE_REFRESH_DELAY_MS);
  });

  /**
   * Subscribe to blockchain events (now a no-op, provider manages subscriptions globally)
   */
  async function subscribe(): Promise<void> {
    const currentAccount = accounts.selectedAccount.value;
    if (!currentAccount) {
      logWarn('useBlockchainEvents', 'no account selected, skipping WebSocket subscriptions');
      return;
    }

    if (currentAccount.providerId !== ACCOUNT_PROVIDER_ID.REOWN_EVM_ACCOUNT_PROVIDER) {
      return;
    }

    // The provider subscribes to events globally during initialization
    // This function now only sets up the helper functions (already done above)
    logDEBUG('WS: Subscribe requested (events managed by EvmPredictionMarketProvider)');
  }

  /**
   * Unsubscribe from blockchain events (now a no-op, provider manages lifecycle)
   */
  function unsubscribe(): void {
    // The provider manages the WebSocket lifecycle globally
    // We don't unsubscribe here to avoid disrupting other components
    logDEBUG('WS: Unsubscribe requested (ignored - events managed by EvmPredictionMarketProvider)');
  }

  // Note: No onUnmounted cleanup - this is a singleton that persists across navigation
  // WebSocket connections should stay alive during the entire app session

  /**
   * Register a callback to be called immediately when a PlacedPrediction event is detected
   * Returns a cleanup function to unregister the callback
   */
  function onPredictionPlaced(callback: TransactionEventCallback): () => void {
    return registerPredictionPlacedCallback(callback);
  }

  /**
   * Register a callback to be called immediately when a CreatedMarket event is detected
   * Returns a cleanup function to unregister the callback
   */
  function onMarketCreated(callback: TransactionEventCallback): () => void {
    return registerMarketCreatedCallback(callback);
  }

  /**
   * Register a callback to be called immediately when a SettledMarket event is detected
   * Returns a cleanup function to unregister the callback
   */
  function onMarketSettled(callback: TransactionEventCallback): () => void {
    return registerMarketSettledCallback(callback);
  }

  /**
   * Register a callback to be called immediately when a CancelledMarket event is detected
   * Returns a cleanup function to unregister the callback
   */
  function onMarketCancelled(callback: TransactionEventCallback): () => void {
    return registerMarketCancelledCallback(callback);
  }

  /**
   * Register a callback to be called immediately when a CreatedCreator event is detected
   * Returns a cleanup function to unregister the callback
   */
  function onCreatorRegistered(callback: TransactionEventCallback): () => void {
    return registerCreatorRegisteredCallback(callback);
  }

  /**
   * Register a callback to be called immediately when an UpdatedCreator event is detected
   * Returns a cleanup function to unregister the callback
   */
  function onCreatorUpdated(callback: TransactionEventCallback): () => void {
    return registerCreatorUpdatedCallback(callback);
  }

  /**
   * Register a callback to be called immediately when an Approval event is detected
   * Returns a cleanup function to unregister the callback
   */
  function onTokenApproved(callback: TransactionEventCallback): () => void {
    return registerTokenApprovedCallback(callback);
  }

  /**
   * Register a callback to be called immediately when a Token Transfer event is detected
   * Returns a cleanup function to unregister the callback
   */
  function onTokenTransferred(callback: TransactionEventCallback): () => void {
    return registerTokenTransferredCallback(callback);
  }

  /**
   * Execute a blockchain transaction with WebSocket event monitoring and timeout fallback.
   * This helper eliminates boilerplate for the common pattern of:
   * 1. Register event callback before transaction
   * 2. Execute transaction
   * 3. Set timeout fallback after transaction completes
   * 4. Handle success (clear timeout + run callback)
   * 5. Handle errors (cleanup callback and timeout)
   * 6. Handle WebSocket disconnection (immediate fallback)
   *
   * @param eventSubscriber - Function that registers a callback and returns cleanup function
   * @param transactionFn - Async function that executes the blockchain transaction
   * @param onSuccess - Callback to run on success (either from event or timeout)
   * @param options - Configuration options
   * @param options.timeoutMs - Milliseconds to wait before fallback (default: 10000)
   * @param options.logPrefix - Prefix for log messages (default: 'Transaction')
   * @param options.shouldSkipTimeout - Function that returns true to skip timeout fallback (e.g., check status already success)
   * @param options.runImmediately - If true, runs the transaction function immediately without waiting for WebSocket subscription (useful if transaction doesn't trigger WebSocket events)
   *
   * @example
   * await executeWithWebSocketEvent(
   *   blockchainEvents.onPredictionPlaced,
   *   async () => { await makePrediction(...); },
   *   async () => {
   *     submitState.value.status = 'success';
   *     // ... component-specific logic
   *   },
   *   {
   *     timeoutMs: WEBSOCKET_EVENT_TIMEOUT_MS,
   *     logPrefix: 'MakePredictionDrawer',
   *     shouldSkipTimeout: () => submitState.value.status === 'success'
   *   }
   * );
   */
  async function executeWithWebSocketEvent(
    eventSubscriber: (callback: TransactionEventCallback) => () => void,
    transactionFn: () => Promise<void>,
    onSuccess: () => void | Promise<void>,
    options?: {
      timeoutMs?: number;
      logPrefix?: string;
      shouldSkipTimeout?: () => boolean;
      runImmediately?: boolean;
    },
  ): Promise<void> {
    const timeoutMs = options?.timeoutMs ?? WEBSOCKET_EVENT_TIMEOUT_MS;
    const logPrefix = options?.logPrefix ?? 'Transaction';

    let unregisterCallback: (() => void) | null = null;
    let eventTimeout: ReturnType<typeof setTimeout> | null = null;
    let successHandled = false;

    // Cleanup function to clear all resources
    const cleanup = () => {
      if (eventTimeout) {
        clearTimeout(eventTimeout);
        eventTimeout = null;
      }
      if (unregisterCallback) {
        unregisterCallback();
        unregisterCallback = null;
      }
    };

    // Success handler that prevents duplicate calls and cleans up
    const handleSuccess = async (triggeredByWebSocket = true) => {
      if (successHandled) return;
      successHandled = true;
      cleanup();
      await onSuccess();

      // If not triggered by WebSocket event, manually trigger balance refresh
      // (WebSocket events have built-in balance refresh, fallbacks don't)
      if (!triggeredByWebSocket) {
        const { triggerBalanceRefresh } = getRefreshTriggers();
        const clearCache = getClearCacheFunction();
        clearCache();
        setTimeout(() => triggerBalanceRefresh(), BALANCE_REFRESH_DELAY_MS);
      }
    };

    try {
      // Register callback to listen for WebSocket event
      unregisterCallback = eventSubscriber(() => handleSuccess(true));

      // Execute the transaction (includes MetaMask confirmation + mining)
      await transactionFn();

      // Check if WebSocket is subscribed (from provider)
      const isSubscribed = getWsIsSubscribed();

      // After transaction completes, check if WebSocket event was received
      // If not, set a short timeout as fallback
      if (!successHandled) {
        eventTimeout = setTimeout(() => {
          // Check if we should skip timeout (e.g., already succeeded)
          if (options?.shouldSkipTimeout?.() === true) {
            cleanup();
            return;
          }

          logDEBUG(
            `${logPrefix}: Timeout fallback - WebSocket event not received within ${timeoutMs}ms after transaction, proceeding with success callback`,
          );
          handleSuccess(false); // false = not from WebSocket, needs manual balance refresh
        }, timeoutMs);
      }

      // If WebSocket not subscribed or if options specify to run immediately, skip waiting for event and run success callback.
      if (!isSubscribed || options?.runImmediately) {
        cleanup();
        logDEBUG(`${logPrefix}: WebSocket not subscribed, using immediate fallback`);
        await handleSuccess(false); // false = not from WebSocket, needs manual balance refresh
      }
    } catch (error) {
      // On any error, cleanup and rethrow
      cleanup();
      throw error;
    }
  }

  return {
    subscribe,
    unsubscribe,
    isSubscribed: computed(() => getWsIsSubscribed()), // Get subscription state from provider
    connectionQuality: computed(() => getWsConnectionQuality()), // Get connection quality from provider
    onPredictionPlaced,
    onMarketCreated,
    onMarketSettled,
    onMarketCancelled,
    onCreatorRegistered,
    onCreatorUpdated,
    onTokenApproved,
    onTokenTransferred,
    executeWithWebSocketEvent,
  };
}

// Export cleanup function for app-level cleanup (now a no-op)
export function cleanupBlockchainEvents(): void {
  // Provider manages WebSocket lifecycle globally
  logDEBUG('WS: Cleanup requested (ignored - events managed by EvmPredictionMarketProvider)');
}
